import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
  Button,
  FormSection,
  Message,
} from '@zolteam/zol-admin-ui';
import { Form, FormElements } from '@zolteam/zol-react-form';
import { useMutation } from '@tanstack/react-query';
// components
import UploadFile from '../../atoms/uploadFile/UploadFile';
// locales
import fr from '../../../locales/fr.json';
// services
import { createPassenger, updatePassenger } from '../../../services/customerPassengers/customerPassengersService';

const PASSENGER_TYPES = [
  'adult',
  'child',
  'infant',
  'animal',
  'group',
];

const FORM_NAME = 'newPassenger';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Passenger',
  properties: {
    category: {
      type: 'string',
      enum: PASSENGER_TYPES,
      description: i18next.t('passengerForm.fields.category'),
      x_layout: '1/1/1/7',
    },
    fullname: {
      type: 'string',
      description: i18next.t('passengerForm.fields.fullname'),
      x_layout: '2/1/2/7',
      minLength: '1',
    },
  },
  required: ['category', 'fullname'],
  additionalProperties: false,
};

/**
 * Empty document object
 * @type {{file: File, fileUuid: string}}
 */
const emptyDoc = {
  file: null,
  fileUuid: null,
};

/**
 * Default values for the form
 * @type {{category: "group" | "adult" | "child" | "infant" | "animal", fullname: string}}
 */
const defaultValues = {
  category: 'adult',
  fullname: '',
};

/**
 * Passenger form component
 * @param {object} props
 * @param {number} props.customerId
 * @param {() => void} props.onSuccess
 * @param {boolean} [props.isUpdate]
 * @param {typeof defaultValues &
 * { id?: number, identityFiles?: string[], identityFileUuids?: string[] }} [props.initialValues]
 * @returns {JSX.Element}
 */
function PassengerNew({
  customerId,
  onSuccess,
  isUpdate = false,
  initialValues = defaultValues,
}) {
  const { t } = useTranslation();
  const [identityFiles, setIdentityFiles] = useState([emptyDoc]);
  const [initialFiles, setInitialFiles] = useState(initialValues?.identityFiles || []);

  /**
   * @param {number} index
   */
  const handleDeleteDocument = (index) => {
    setInitialFiles((state) => state.filter((_, i) => i !== index));
  };

  const {
    mutate: updatePassengerMutate,
    isError: updatePassengerIsError,
    error: updatePassengerError,
  } = useMutation(updatePassenger, {
    onSuccess: () => {
      setInitialFiles([]);
      setIdentityFiles([emptyDoc]);
      onSuccess();
    },
  });

  const {
    mutate: createPassengerMutate,
    isError: createPassengerIsError,
    error: createPassengerError,
  } = useMutation(
    createPassenger,
    {
      onSuccess: () => {
        onSuccess();
      },
    },
  );

  /**
   * Handle form submit
   * @param {typeof defaultValues} formValues
   */
  const handleSubmit = (formValues) => {
    const newFiles = identityFiles.map((doc) => doc.fileUuid)?.filter((file) => !!file);
    const initialFileUuids = initialFiles?.map((url) => initialValues.identityFileUuids?.find(
      (uuid) => url?.includes(uuid),
    ));
    const payload = {
      fullname: formValues.fullname,
      category: formValues.category,
      identityFileUuids: newFiles.concat(initialFileUuids)?.filter((uuid) => !!uuid),
    };

    if (isUpdate) {
      updatePassengerMutate({
        customerId,
        passenger: Object.assign(payload, { id: initialValues?.id }),
      });
    } else {
      createPassengerMutate({
        customerId,
        passenger: payload,
      });
    }
  };

  const handleAddDocumentForm = () => {
    setIdentityFiles((state) => state?.concat([null]));
  };

  /**
   * Handle file change
   * @param {number} index
   * @param {File[]} file
   * @param {string} fileUuid
   */
  const handleFileChange = (index, file, fileUuid) => {
    setIdentityFiles((state) => state.map(
      (doc, i) => (i === index
        ? { file: file?.[0], fileUuid } : doc),
    ));
  };

  useEffect(() => {
    setInitialFiles(initialValues?.identityFiles || []);
  }, [initialValues]);

  return (
    <div className="d-flex f-column gap-m">
      {createPassengerIsError || updatePassengerIsError ? (
        <div className="margin-bottom-m">
          <Message>
            {t(`apiErrors.${createPassengerError
            // @ts-ignore
              ?.code || updatePassengerError?.code}`)}
          </Message>
        </div>
      ) : null}
      <Form
        key={isUpdate ? JSON.stringify(initialValues) : 'new'}
        name={FORM_NAME}
        className=""
        callbacks={{
          onSubmit: handleSubmit,
        }}
        schema={SCHEMA}
        translations={{
          formValidation: fr.formValidation,
        }}
        defaultValues={initialValues}
      >
        {(formConfig, formInfo) => (
          <div>
            <FormElements
              translations={{
                selectOptionLabels: {
                  adult: t('customerList.passengers.categories.adult'),
                  child: t('customerList.passengers.categories.child'),
                  infant: t('customerList.passengers.categories.infant'),
                  animal: t('customerList.passengers.categories.animal'),
                  group: t('customerList.passengers.categories.group'),
                },
                mediaGallery: null,
              }}
              formConfig={formConfig}
              formTitle={formInfo.formName}
            />

            {/* Display existing documents if in update mode */}
            {isUpdate && initialFiles?.length > 0 ? (
              <FormSection
                className="margin-bottom-s"
                label={t('passengerForm.documents.title')}
              >
                <div className="d-flex f-column gap-xs">
                  {initialFiles?.map((fileUrl, index) => (
                    <div key={fileUrl} className="d-flex f-row align-center justify-between">
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        {t('customerList.passengers.fields.file', { nb: index + 1 })}
                      </a>
                      <Button
                        color="alert"
                        icon="trash"
                        size="small"
                        onClick={() => handleDeleteDocument(index)}
                      />
                    </div>
                  ))}
                </div>
              </FormSection>
            ) : null}

            {identityFiles?.map((fileObject, index) => (
              <div key={fileObject ? JSON.stringify(fileObject) : index}>
                <FormSection
                  className="margin-bottom-s"
                  label={t('bookingAddDocument.fields.docNumber', { nb: 0 + 1 })}
                >
                  <UploadFile
                    hasFile={!!fileObject?.file}
                    file={fileObject?.file}
                    noFileChildren={t('passengerForm.fields.dropFile')}
                    onDrop={(newFile, newId) => handleFileChange(index, newFile, newId)}
                    index={index}
                  />
                </FormSection>
                {index + 1 === identityFiles?.length && !!identityFiles[0].fileUuid ? (
                  <Button
                    className="justify-end"
                    color="neutral00"
                    icon="add"
                    size="small"
                    onClick={handleAddDocumentForm}
                  >
                    {t('bookingAddDocument.actions.addOther')}
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Form>
      <Button
        form={FORM_NAME}
        isSubmit
        isLoading={false}
      >
        {t(isUpdate ? 'passengerForm.actions.update' : 'passengerForm.actions.add')}
      </Button>
    </div>
  );
}

export default PassengerNew;
